<template>
  <d-modal @close="handleClose" size="lg">
    <d-modal-header>
      <d-modal-title>
        {{ $t('order.priceChangePopup.title') }}
      </d-modal-title>
    </d-modal-header>
    <d-modal-body class="px-4">
      <h6
        v-if="Object.keys(changedProductPrices).length"
        class="mb-2">
        {{ $t('order.priceChangePopup.productsTitle') }}
      </h6>

      <table
        v-if="Object.keys(changedProductPrices).length"
        class="table table-sm mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0" style="width: 100px;"><small>{{ $t('order.priceChangePopup.status') }}</small></th>
            <th scope="col" class="border-0" style="width: 300px;"><small>{{ $t('order.priceChangePopup.productName') }}</small></th>
            <th scope="col" class="border-0 text-center"><small>{{ $t('order.priceChangePopup.priceChange') }}</small></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(itemData, index) of changedProductPrices"
            :key="'product-data-' + index">
            <td class="align-middle">
              <d-badge
                v-if="itemData.applied"
                theme="success">
                {{ $t('order.priceChangePopup.applied') }}
              </d-badge>
              <d-badge
                v-if="!itemData.applied"
                theme="danger">
                {{ $t('order.priceChangePopup.notApplied') }}
              </d-badge>
            </td>
            <td class="align-middle">
              <small>{{ itemData.name }}</small>
            </td>
            <td class="align-middle text-center">
              <span
                v-if="!itemData.for_free && !itemData.use_custom_price"
                :class="itemData.oldPrice !== itemData.newPrice ? 'text-danger' : 'text-gray'">
                {{ Number(itemData.oldPrice).toFixed(2) }} &rarr; {{ Number(itemData.newPrice).toFixed(2) }}
              </span>

              <d-badge
                v-if="itemData.for_free"
                theme="success">
                {{ $t('order.forFree') }}
              </d-badge>

              <d-badge
                v-if="itemData.use_custom_price"
                theme="primary">
                {{ $t('order.customPrice') }}
              </d-badge>
            </td>
          </tr>
        </tbody>
      </table>

      <h6
        v-if="Object.keys(changedProductServicePrices).length"
        class="mt-4 mb-2">
        {{ $t('order.priceChangePopup.servicesTitle') }}
      </h6>

      <table
        v-if="Object.keys(changedProductServicePrices).length"
        class="table table-sm mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0" style="width: 100px;"><small>{{ $t('order.priceChangePopup.status') }}</small></th>
            <th scope="col" class="border-0"><small>{{ $t('order.priceChangePopup.serviceName') }}</small></th>
            <th scope="col" class="border-0 text-center" style="width: 120px;"><small>{{ $t('order.priceChangePopup.multiplierChange') }}</small></th>
            <th scope="col" class="border-0 text-center" style="width: 160px;"><small>{{ $t('order.priceChangePopup.priceChange') }}</small></th>
            <th scope="col" class="border-0 text-center" style="width: 160px;"><small>{{ $t('order.priceChangePopup.priceSetupChange') }}</small></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(itemData, index) of changedProductServicePrices"
            :key="'service-product-data-' + index">
            <td class="align-middle">
              <d-badge
                v-if="itemData.applied"
                theme="success">
                {{ $t('order.priceChangePopup.applied') }}
              </d-badge>
              <d-badge
                v-if="!itemData.applied"
                theme="danger">
                {{ $t('order.priceChangePopup.notApplied') }}
              </d-badge>
            </td>
            <td class="align-middle">
              <small>{{ itemData.name }}</small>
            </td>
            <td class="align-middle text-center">
              <span :class="itemData.oldMultiplier !== itemData.newMultiplier ? 'text-danger' : 'text-gray'">
                {{ itemData.oldMultiplier }} &rarr; {{ itemData.newMultiplier }}
              </span>
            </td>
            <td class="align-middle text-center">
              <span
                v-if="!itemData.use_custom_price"
                :class="itemData.oldPrice !== itemData.newPrice ? 'text-danger' : 'text-gray'">
                {{ Number(itemData.oldPrice).toFixed(2) }} &rarr; {{ Number(itemData.newPrice).toFixed(2) }}
              </span>

              <d-badge
                v-if="itemData.use_custom_price"
                theme="primary">
                {{ $t('order.customPrice') }}
              </d-badge>
            </td>
            <td class="align-middle text-center">
              <span
                v-if="!itemData.use_custom_price"
                :class="itemData.oldSetupPrice !== itemData.newSetupPrice ? 'text-danger' : 'text-gray'">
                {{ Number(itemData.oldSetupPrice).toFixed(2) }} &rarr; {{ Number(itemData.newSetupPrice).toFixed(2) }}
              </span>

              <d-badge
                v-if="itemData.use_custom_price"
                theme="primary">
                {{ $t('order.customPrice') }}
              </d-badge>
            </td>
          </tr>
        </tbody>
      </table>

      <h6
        v-if="Object.keys(changedGlobalServicePrices).length"
        class="mt-4 mb-2">
        {{ $t('order.priceChangePopup.globalServicesTitle') }}
      </h6>

      <table
        v-if="Object.keys(changedGlobalServicePrices).length"
        class="table table-sm mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0" style="width: 100px;"><small>{{ $t('order.priceChangePopup.status') }}</small></th>
            <th scope="col" class="border-0"><small>{{ $t('order.priceChangePopup.globalServiceName') }}</small></th>
            <th scope="col" class="border-0 text-center" style="width: 120px;"><small>{{ $t('order.priceChangePopup.multiplierChange') }}</small></th>
            <th scope="col" class="border-0 text-center" style="width: 160px;"><small>{{ $t('order.priceChangePopup.priceChange') }}</small></th>
            <th scope="col" class="border-0 text-center" style="width: 160px;"><small>{{ $t('order.priceChangePopup.priceSetupChange') }}</small></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(itemData, index) of changedGlobalServicePrices"
            :key="'global-service-data-' + index">
            <td class="align-middle">
              <d-badge
                v-if="itemData.applied"
                theme="success">
                {{ $t('order.priceChangePopup.applied') }}
              </d-badge>
              <d-badge
                v-if="!itemData.applied"
                theme="danger">
                {{ $t('order.priceChangePopup.notApplied') }}
              </d-badge>
            </td>
            <td class="align-middle">
              <small>{{ itemData.name }}</small>
            </td>
            <td class="align-middle text-center">
              <span :class="itemData.oldMultiplier !== itemData.newMultiplier ? 'text-danger' : 'text-gray'">
                {{ itemData.oldMultiplier }} &rarr; {{ itemData.newMultiplier }}
              </span>
            </td>
            <td class="align-middle text-center">
              <span
                v-if="!itemData.use_custom_price"
                :class="itemData.oldPrice !== itemData.newPrice ? 'text-danger' : 'text-gray'">
                {{ Number(itemData.oldPrice).toFixed(2) }} &rarr; {{ Number(itemData.newPrice).toFixed(2) }}
              </span>

              <d-badge
                v-if="itemData.use_custom_price"
                theme="primary">
                {{ $t('order.customPrice') }}
              </d-badge>
            </td>
            <td class="align-middle text-center">
              <span
                v-if="!itemData.use_custom_price"
                :class="itemData.oldSetupPrice !== itemData.newSetupPrice ? 'text-danger' : 'text-gray'">
                {{ Number(itemData.oldSetupPrice).toFixed(2) }} &rarr; {{ Number(itemData.newSetupPrice).toFixed(2) }}
              </span>

              <d-badge
                v-if="itemData.use_custom_price"
                theme="primary">
                {{ $t('order.customPrice') }}
              </d-badge>
            </td>
          </tr>
        </tbody>
      </table>
    </d-modal-body>
    <d-modal-footer>
      <d-button
        class="w-50 ml-auto mr-auto"
        theme="success"
        @click.prevent="handleClose">
        {{ $t('ui.ok')}}
      </d-button>
    </d-modal-footer>
  </d-modal>
</template>

<script>
export default {
  name: 'order-price-change-popup',
  props: [
    'changedProductPrices',
    'changedProductServicePrices',
    'changedGlobalServicePrices'
  ],
  methods: {
    handleClose () {
      this.$bus.$emit('close-price-changes-popup');
    },
    cancel () {
      this.handleClose();
    }
  }
}
</script>
